// src/pages/ContentTasksPage.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot, getDocs } from 'firebase/firestore';
import { db } from '../firebase'; // Adjust path
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/styleUtils';
import { useSocialLanguage } from '../hooks/useSocialLanguage';
import LanguageToggle from '../components/LanguageToggle';
import { getStatusLabel, getPlatformLabel, getContentTypeLabel } from '../utils/socialEnumLabels';
import styles from '../styles/ContentTasksPage.module.css';

const TRANSLATIONS = {
    en: {
        pageTitle: 'Social Media Content Tasks',
        newContentTask: '+ New Content Task',
        noPermission: 'You do not have permission to view content tasks.',
        statusLabel: 'Status',
        clientLabel: 'Client',
        platformLabel: 'Platform',
        searchLabel: 'Search',
        searchPlaceholder: 'Search title or caption...',
        overdueOnly: 'Overdue only',
        all: 'All',
        unnamed: 'Unnamed',
        loadingTasks: 'Loading content tasks...',
        colTitle: 'Title',
        colClient: 'Client',
        colPlatform: 'Platform',
        colType: 'Type',
        colStatus: 'Status',
        colDueDate: 'Due Date',
        colAssignees: 'Assignees',
        noTasksFound: 'No content tasks found.',
        untitled: 'Untitled',
        notAvailable: 'N/A',
        unassigned: 'Unassigned',
        failedToLoad: 'Failed to load content tasks.',
    },
    ar: {
        pageTitle: 'مهام محتوى السوشيال ميديا',
        newContentTask: '+ مهمة محتوى جديدة',
        noPermission: 'ليس لديك صلاحية لعرض مهام المحتوى.',
        statusLabel: 'الحالة',
        clientLabel: 'العميل',
        platformLabel: 'المنصة',
        searchLabel: 'بحث',
        searchPlaceholder: 'بحث بالعنوان أو النص...',
        overdueOnly: 'المتأخرة فقط',
        all: 'الكل',
        unnamed: 'بدون اسم',
        loadingTasks: 'جاري تحميل مهام المحتوى...',
        colTitle: 'العنوان',
        colClient: 'العميل',
        colPlatform: 'المنصة',
        colType: 'النوع',
        colStatus: 'الحالة',
        colDueDate: 'تاريخ الاستحقاق',
        colAssignees: 'المكلفون',
        noTasksFound: 'لا توجد مهام محتوى.',
        untitled: 'بدون عنوان',
        notAvailable: 'غير متاح',
        unassigned: 'غير معين',
        failedToLoad: 'فشل تحميل مهام المحتوى.',
    },
};

// Exact 13-status enum, exact order
const STATUS_OPTIONS = [
    'New Request',
    'Content Writing',
    'Content Ready',
    'Design / Editing',
    'Design Ready',
    'Internal Review',
    'Changes Requested',
    'Ready for Client Approval',
    'Client Approval',
    'Approved',
    'Scheduled',
    'Published',
    'Cancelled',
];

const PLATFORM_OPTIONS = ['Facebook', 'Instagram', 'TikTok', 'LinkedIn', 'X', 'YouTube', 'WhatsApp Channel'];

// Roles allowed to access this page / create new content tasks
const PAGE_ACCESS_ROLES = ['Manager', 'Accounts Manager', 'Sales', 'Content Creator'];
const CREATE_ROLES = ['Manager', 'Accounts Manager', 'Sales', 'Content Creator'];

const NON_OVERDUE_STATUSES = ['Published', 'Cancelled'];

// Maps a status string to a CSS class suffix (used for color-coded badges)
const getStatusClassSuffix = (status) => {
    switch (status) {
        case 'New Request':
        case 'Content Writing':
        case 'Content Ready':
            return 'InProgress'; // blue-ish
        case 'Design / Editing':
        case 'Design Ready':
            return 'Design'; // purple-ish
        case 'Internal Review':
        case 'Changes Requested':
            return 'Review'; // orange/amber
        case 'Ready for Client Approval':
        case 'Client Approval':
            return 'ClientApproval'; // yellow
        case 'Approved':
        case 'Scheduled':
            return 'Scheduled'; // teal
        case 'Published':
            return 'Published'; // green
        case 'Cancelled':
            return 'Cancelled'; // gray/red
        default:
            return 'Default';
    }
};

const ContentTasksPage = () => {
    const { userRole } = useAuth();
    const { language, toggleLanguage, isRTL } = useSocialLanguage();
    const t = TRANSLATIONS[language];

    const [tasks, setTasks] = useState([]);
    const [clients, setClients] = useState([]);
    const [usersMap, setUsersMap] = useState(new Map());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [statusFilter, setStatusFilter] = useState('All');
    const [clientFilter, setClientFilter] = useState('All');
    const [platformFilter, setPlatformFilter] = useState('All');
    const [searchText, setSearchText] = useState('');
    const [overdueOnly, setOverdueOnly] = useState(false);

    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    // Real-time fetch of contentTasks, ordered by dueDate ascending? Spec: ordered by dueDate or createdAt desc.
    useEffect(() => {
        setLoading(true);
        setError('');
        const tasksCol = collection(db, 'contentTasks');
        const q = query(tasksCol, orderBy('createdAt', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            if (!isMounted.current) return;
            const list = snapshot.docs.map((docSnap) => {
                const data = docSnap.data();
                return {
                    id: docSnap.id,
                    ...data,
                    dueDate: data.dueDate?.toDate ? data.dueDate.toDate() : null,
                    scheduledDate: data.scheduledDate?.toDate ? data.scheduledDate.toDate() : null,
                    publishedDate: data.publishedDate?.toDate ? data.publishedDate.toDate() : null,
                    createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : null,
                };
            });
            setTasks(list);
            setLoading(false);
        }, (err) => {
            if (!isMounted.current) return;
            console.error('Error fetching content tasks:', err);
            setError('failedToLoad');
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // One-time fetch of clients for the filter dropdown
    useEffect(() => {
        const fetchClients = async () => {
            try {
                const clientsCol = collection(db, 'clients');
                const q = query(clientsCol, orderBy('name', 'asc'));
                const snapshot = await getDocs(q);
                if (!isMounted.current) return;
                setClients(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
            } catch (err) {
                console.error('Error fetching clients for filter:', err);
            }
        };
        fetchClients();
    }, []);

    // One-time fetch of users to resolve assignee uids to names
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const usersCol = collection(db, 'users');
                const snapshot = await getDocs(usersCol);
                if (!isMounted.current) return;
                const map = new Map();
                snapshot.docs.forEach((d) => {
                    const data = d.data();
                    map.set(d.id, data.name || data.email || 'Unknown');
                });
                setUsersMap(map);
            } catch (err) {
                console.error('Error fetching users:', err);
            }
        };
        fetchUsers();
    }, []);

    const resolveAssigneeNames = (assignedTo) => {
        if (!Array.isArray(assignedTo) || assignedTo.length === 0) return t.unassigned;
        return assignedTo.map((uid) => usersMap.get(uid) || uid).join(', ');
    };

    const isOverdue = (task) => {
        if (!task.dueDate) return false;
        if (NON_OVERDUE_STATUSES.includes(task.status)) return false;
        return task.dueDate < new Date();
    };

    const filteredTasks = useMemo(() => {
        const now = new Date();
        return tasks.filter((task) => {
            if (statusFilter !== 'All' && task.status !== statusFilter) return false;
            if (clientFilter !== 'All' && task.clientId !== clientFilter) return false;
            if (platformFilter !== 'All' && !(task.platforms || [task.platform]).includes(platformFilter)) return false;
            if (overdueOnly) {
                const overdue = task.dueDate && !NON_OVERDUE_STATUSES.includes(task.status) && task.dueDate < now;
                if (!overdue) return false;
            }
            if (searchText.trim()) {
                const term = searchText.trim().toLowerCase();
                const titleMatch = task.title?.toLowerCase().includes(term);
                const captionMatch = task.caption?.toLowerCase().includes(term);
                if (!titleMatch && !captionMatch) return false;
            }
            return true;
        });
    }, [tasks, statusFilter, clientFilter, platformFilter, overdueOnly, searchText]);

    const canCreate = CREATE_ROLES.includes(userRole);
    const canAccessPage = PAGE_ACCESS_ROLES.includes(userRole);

    if (!canAccessPage) {
        return (
            <div className={styles.pageContainer} dir={isRTL ? 'rtl' : 'ltr'}>
                <div className={styles.errorMessage}>{t.noPermission}</div>
            </div>
        );
    }

    return (
        <div className={styles.pageContainer} dir={isRTL ? 'rtl' : 'ltr'}>
            <div className={styles.header}>
                <h2 className={styles.title}>{t.pageTitle}</h2>
                <LanguageToggle language={language} onToggle={toggleLanguage} />
                {canCreate && (
                    <Link to="/social/tasks/new" className={styles.addButton}>
                        {t.newContentTask}
                    </Link>
                )}
            </div>

            {error && <div className={styles.errorMessage}>{TRANSLATIONS[language][error] || error}</div>}

            <div className={styles.filtersBar}>
                <div className={styles.filterGroup}>
                    <label htmlFor="statusFilter">{t.statusLabel}</label>
                    <select id="statusFilter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="All">{t.all}</option>
                        {STATUS_OPTIONS.map((status) => (
                            <option key={status} value={status}>{getStatusLabel(status, language)}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.filterGroup}>
                    <label htmlFor="clientFilter">{t.clientLabel}</label>
                    <select id="clientFilter" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
                        <option value="All">{t.all}</option>
                        {clients.map((client) => (
                            <option key={client.id} value={client.id}>{client.name || t.unnamed}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.filterGroup}>
                    <label htmlFor="platformFilter">{t.platformLabel}</label>
                    <select id="platformFilter" value={platformFilter} onChange={(e) => setPlatformFilter(e.target.value)}>
                        <option value="All">{t.all}</option>
                        {PLATFORM_OPTIONS.map((platform) => (
                            <option key={platform} value={platform}>{getPlatformLabel(platform, language)}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.filterGroup}>
                    <label htmlFor="searchText">{t.searchLabel}</label>
                    <input
                        type="text"
                        id="searchText"
                        placeholder={t.searchPlaceholder}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </div>

                <div className={styles.filterGroup}>
                    <label className={styles.toggleLabel}>
                        <input
                            type="checkbox"
                            checked={overdueOnly}
                            onChange={(e) => setOverdueOnly(e.target.checked)}
                        />
                        {t.overdueOnly}
                    </label>
                </div>
            </div>

            {loading ? (
                <div className={styles.loadingIndicator}>{t.loadingTasks}</div>
            ) : (
                <div className={styles.tableContainer}>
                    <table className={styles.taskTable}>
                        <thead className={styles.tableHeader}>
                            <tr>
                                <th>{t.colTitle}</th>
                                <th>{t.colClient}</th>
                                <th>{t.colPlatform}</th>
                                <th>{t.colType}</th>
                                <th>{t.colStatus}</th>
                                <th>{t.colDueDate}</th>
                                <th>{t.colAssignees}</th>
                            </tr>
                        </thead>
                        <tbody className={styles.tableBody}>
                            {filteredTasks.length === 0 && (
                                <tr>
                                    <td colSpan="7" className={styles.noDataCell}>{t.noTasksFound}</td>
                                </tr>
                            )}
                            {filteredTasks.map((task) => (
                                <tr key={task.id}>
                                    <td data-label={`${t.colTitle}:`}>
                                        <Link to={`/social/tasks/${task.id}`} className={styles.taskTitleLink}>
                                            {task.title || t.untitled}
                                        </Link>
                                    </td>
                                    <td data-label={`${t.colClient}:`}>{task.clientName || t.notAvailable}</td>
                                    <td data-label={`${t.colPlatform}:`}>{task.platform ? getPlatformLabel(task.platform, language) : t.notAvailable}</td>
                                    <td data-label={`${t.colType}:`}>{task.contentType ? getContentTypeLabel(task.contentType, language) : t.notAvailable}</td>
                                    <td data-label={`${t.colStatus}:`}>
                                        <span className={`${styles.statusBadge} ${styles[`status${getStatusClassSuffix(task.status)}`]}`}>
                                            {task.status ? getStatusLabel(task.status, language) : t.notAvailable}
                                        </span>
                                    </td>
                                    <td data-label={`${t.colDueDate}:`} className={isOverdue(task) ? styles.overdueDate : ''}>
                                        {task.dueDate ? formatDate(task.dueDate) : t.notAvailable}
                                    </td>
                                    <td data-label={`${t.colAssignees}:`}>{resolveAssigneeNames(task.assignedTo)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ContentTasksPage;
